import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of, firstValueFrom } from 'rxjs';
import { map, catchError, tap, shareReplay, filter, take } from 'rxjs/operators';
import { ResolverService } from './resolver.service';
import { StorageService } from './storage.service';

export interface AdminAdditionalData {
  client_id?: number | null;
  client_uuid?: string | null;
  org_whatsapp_web_id?: number | null;
  raw?: any;
}

@Injectable({
  providedIn: 'root'
})
export class AdminDataService {
  private resolver = inject(ResolverService);
  private storage = inject(StorageService);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);

  private adminDataSubject = new BehaviorSubject<AdminAdditionalData | null>(null);
  public adminData$: Observable<AdminAdditionalData | null> = this.adminDataSubject.asObservable();

  private allAdditionalDataSubject = new BehaviorSubject<any[] | null>(null);
  public allAdditionalData$: Observable<any[] | null> = this.allAdditionalDataSubject.asObservable();

  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  public isLoading$: Observable<boolean> = this.isLoadingSubject.asObservable();

  private errorSubject = new BehaviorSubject<any>(null);
  public error$: Observable<any> = this.errorSubject.asObservable();

  private fetchObservable: Observable<any[]> | null = null;
  private isFetched = false;

  constructor() {}

  /**
   * Determine if current context / user is Admin
   */
  public isAdmin(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    const routerUrl = this.router.url || '';
    const browserPath = (typeof window !== 'undefined' && window.location) ? window.location.pathname : '';
    const currentUrl = ((routerUrl && routerUrl !== '/') ? routerUrl : browserPath).toLowerCase();

    // STRICT: Only return true when actively on an /admin route
    // Any regular user route (e.g. /user/*) will always return false
    return currentUrl.startsWith('/admin') || currentUrl.includes('/admin/');
  }

  /**
   * Load additional data globally. Deduplicates requests so it only fetches once.
   */
  public loadAdminAdditionalData(forceRefresh: boolean = false): Observable<any[]> {
    if (!isPlatformBrowser(this.platformId)) {
      return of([]);
    }

    if (this.isFetched && !forceRefresh && this.allAdditionalDataSubject.value) {
      return of(this.allAdditionalDataSubject.value);
    }

    if (this.fetchObservable && !forceRefresh) {
      return this.fetchObservable;
    }

    this.isLoadingSubject.next(true);
    this.errorSubject.next(null);

    this.fetchObservable = this.resolver.getAllPrompts().pipe(
      map((res: any) => (Array.isArray(res) ? res : (res?.data || []))),
      tap((list: any[]) => {
        this.isFetched = true;
        this.isLoadingSubject.next(false);
        this.allAdditionalDataSubject.next(list);

        // Find record where name === 'admin'
        const adminRecord = list.find((item: any) => item && (item.name === 'admin' || item.name?.toLowerCase() === 'admin'));
        if (adminRecord && adminRecord.data) {
          const rawClientId = adminRecord.data.client_id;
          const rawClientUuid = adminRecord.data.client_uuid;
          const rawOrgWebId = adminRecord.data.org_whatsapp_web_id;

          const client_id = (rawClientId !== undefined && rawClientId !== null && Number(rawClientId) > 0)
            ? Number(rawClientId)
            : null;
          const client_uuid = (rawClientUuid && String(rawClientUuid).trim() !== '')
            ? String(rawClientUuid).trim()
            : null;
          const org_whatsapp_web_id = (rawOrgWebId !== undefined && rawOrgWebId !== null && Number(rawOrgWebId) > 0)
            ? Number(rawOrgWebId)
            : null;

          const adminData: AdminAdditionalData = {
            client_id,
            client_uuid,
            org_whatsapp_web_id,
            raw: adminRecord
          };
          console.log("admin", adminData);
          this.adminDataSubject.next(adminData);
        } else {
          this.adminDataSubject.next(null);
        }
      }),
      catchError((err) => {
        this.isFetched = true;
        this.isLoadingSubject.next(false);
        this.errorSubject.next(err);
        this.adminDataSubject.next(null);
        this.fetchObservable = null;
        console.error('[AdminDataService] Failed to load additional-data:', err);
        return of([]);
      }),
      shareReplay(1)
    );

    return this.fetchObservable;
  }

  /**
   * Get synchronous value of current admin additional data
   */
  public getAdminData(): AdminAdditionalData | null {
    return this.adminDataSubject.value;
  }

  /**
   * Wait for admin data to be loaded and resolved
   */
  public waitForAdminData(): Observable<AdminAdditionalData | null> {
    if (this.isFetched) {
      return of(this.adminDataSubject.value);
    }

    this.loadAdminAdditionalData();

    return this.adminData$.pipe(
      filter(() => this.isFetched),
      take(1)
    );
  }

  /**
   * Helper to get effective client ID:
   * If on Admin side and admin record is loaded, returns admin.client_id.
   * Otherwise falls back to normal client.id from fallbackClient.
   */
  public getEffectiveClientId(fallbackClient?: any): any {
    if (this.isAdmin()) {
      const adminData = this.getAdminData();
      if (adminData && adminData.client_id) {
        return adminData.client_id;
      }
    }
    return fallbackClient?.id || fallbackClient?.client_id || null;
  }

  /**
   * Helper to get effective client UUID:
   * If on Admin side and admin record is loaded, returns admin.client_uuid.
   * Otherwise falls back to normal client uuid from fallbackClient.
   */
  public getEffectiveClientUuid(fallbackClient?: any): any {
    if (this.isAdmin()) {
      const adminData = this.getAdminData();
      if (adminData && adminData.client_uuid) {
        return adminData.client_uuid;
      }
    }
    return fallbackClient?.uuid || fallbackClient?.client_uuid || fallbackClient?.id || null;
  }

  /**
   * Helper to get effective org WhatsApp web ID (if required by specific APIs)
   */
  public getEffectiveOrgWhatsappWebId(fallbackOrgWebId?: any): any {
    if (this.isAdmin()) {
      const adminData = this.getAdminData();
      if (adminData && adminData.org_whatsapp_web_id !== undefined && adminData.org_whatsapp_web_id !== null) {
        return adminData.org_whatsapp_web_id;
      }
    }
    return fallbackOrgWebId ?? null;
  }
}
